import { faChevronDown } from "@jewlr/pro-regular-svg-icons/faChevronDown"
import { faChevronLeft } from "@jewlr/pro-regular-svg-icons/faChevronLeft"
import { faChevronRight } from "@jewlr/pro-regular-svg-icons/faChevronRight"
import { faChevronUp } from "@jewlr/pro-regular-svg-icons/faChevronUp"
import { faMinus } from "@jewlr/pro-regular-svg-icons/faMinus"
import { faPlus } from "@jewlr/pro-regular-svg-icons/faPlus"
import { faRedo } from "@jewlr/pro-regular-svg-icons/faRedo"
import { faUndo } from "@jewlr/pro-regular-svg-icons/faUndo"
import { faInfoCircle } from "@jewlr/pro-solid-svg-icons/faInfoCircle"
import {
  Box,
  Button,
  Divider,
  Flex,
  FontAwesomeIcon,
  Image as Img, // Renamed to avoid conflict with the HTMLImageElement Image
  MediaFlex,
  Typography,
} from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import { useEffect, useRef } from "react"
import { connect } from "react-redux"
import styled from "styled-components"

import { Footnote, FootnoteText } from "areas/product/jewlr/components"
import { TipText } from "areas/product/jewlr/modals/styled-components"
import PhotoUpload from "areas/product/jewlr/photo-upload"
import { cropUploadUserImage, updateChoice } from "areas/product/store/actions"
import Cropper from "components/cropper"
import {
  getImgPixels,
  copyImg,
  updateImgThreshold,
  thresholdSettings,
} from "helpers/image"
import StorageHelper from "helpers/local-storage-cache"

const Container = styled(Box).attrs({
  width: "100%",
})`
  overflow: hidden;
`

const ConfirmButton = styled(Button).attrs({
  variant: "secondary",
})`
  font-size: 14px;
  color: "#fff";
  height: 44px;
  min-width: 165px;
  font-weight: 500;

  background-color: ${(props) => props.theme.colors.primary};
  width: 100%;
  &.uploading {
    background-color: #646464;
    transition: background-color 0.5s !important;
    i {
      margin-right: 4px;
      font-weight: bold;
      color: "#fff";
    }
  }
`

const SelectButton = styled(Button).attrs({
  variant: "primary",
})`
  cursor: pointer;
  font-size: 18px;
  font-weight: 500;
  height: 44px;
  line-height: 16px;
  text-align: center;
  width: 100%;
  &:disabled {
    background-color: #a0a0a0;
    color: "#FFFFFF";
  }
`

const ProductImageCropWrapper = styled.div`
  background: ${(props) =>
    props.background ? `url(${props.background})` : "none"};
  background-repeat: no-repeat;
  background-size: cover;
  max-width: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
  ${(props) => props.theme.mediaQueries.tablet`
    min-width: ${ImgBoxLength};
    width: ${ImgBoxLength};
  `}
  &:after {
    content: "";
    display: block;
    padding-bottom: 100%;
  }
`

const ProductImageCrop = styled.div`
  height: 100%;
  position: absolute;
  width: 100%;
`

const OriginalImageHidden = styled.div`
  height: 100%;
  opacity: 0;
  position: absolute;
  width: 100%;
  z-index: -1;
`

const CropOverlay = styled.div`
  box-shadow: 2px 2px white;
  height: 100%;
  pointer-events: none;
  position: absolute;
  width: 100%;
  z-index: 2;
`

const CropperInstruction = styled.div`
  background-color: #111;
  border-radius: 4px;
  bottom: 14px;
  box-shadow: 0 0.5rem 1rem -0.5rem black;

  color: "#fff";
  display: none;
  font-size: 12px;
  padding: 10px;
  position: absolute;
  right: 0;
  text-align: left;
  width: 200px;
  z-index: 2;
  &:before {
    background: inherit;
    content: " ";
    display: block;
    height: 10px;
    left: 32%;
    margin-left: -5px;
    position: absolute;
    top: -3px;
    transform: rotate(45deg);
    width: 10px;
  }
  p {
    margin: 0;
  }
`

const CropperEditHeading = styled.div`
  margin-bottom: 12px;
  margin-top: 24px;
  text-align: center;

  &:after {
    clear: both;
    content: "";
    display: table;
  }
`

const CropperActionTitle = styled(Typography).attrs({
  display: { _: "none", tablet: "block" },
  fontSize: 16,
  fontWeight: 300,
  letterSpacing: "-0.02em",
  lineHeight: "22px",
})``

const CropperActionSubtitle = styled(Typography).attrs((props) => ({
  color: props.theme.colors.neutral,
  display: { _: "none", tablet: "block" },
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: "-0.02em",
  lineHeight: "22px",
  ...props,
}))``

const CropTips = styled.div`
  cursor: pointer;
  @media (min-width: 768px) {
    float: right;
  }
  i {
    color: #4268ff;
  }
  span {
    font-size: 16px;
    font-style: italic;
    text-align: right;
    @media (min-width: 768px) {
      font-size: 16px;
    }
  }
`

const CropperMobileActions = styled(Box)`
  border-bottom: 1px solid #a0a0a0;
  margin-bottom: 16px;
  padding-bottom: 10px;
  touch-action: manipulation;
  @media (min-width: 768px) {
    display: none !important;
  }

  &:after {
    clear: both;
    content: "";
    display: table;
  }
`

const CropperActions = styled(MediaFlex).attrs((props) => ({
  flexWrap: "wrap",
  gap: "12px",
  greaterThanOrEqual: "tablet",
  width: "auto",
  ...props,
}))`
  align-items: center;
  justify-content: center;
  text-align: justify;

  -webkit-user-select: none;
  -moz-user-select: none;
  -khtml-user-select: none;
  -ms-user-select: none;
`

const CropperActionPair = styled(Flex).attrs({
  gap: "6px",
})`
  align-items: center;
  justify-content: center;
`

const ActionButton = styled(Button).attrs({
  variant: "text",
})`
  align-items: center;
  border-radius: 50%;
  border: 1px solid ${(props) => props.theme.colors.grey[10]};
  height: 48px;
  justify-content: center;
  width: 48px;
  ${(props) => props.theme.mediaQueries.tablet`
    height: 40px;
    width: 40px;
  `}
`

const SquareActionButton = styled(Button).attrs((props) => ({
  color: props.theme.colors.black,
  variant: "text",
}))`
  align-items: center;
  border: 1px solid ${(props) => props.theme.colors.grey[10]};
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 40px;
  height: 48px;
  justify-content: center;
  width: 48px;
  ${(props) => props.theme.mediaQueries.tablet`
    height: 40px;
    width: 40px;
  `}
  ${(props) =>
    props.selected &&
    `
    border: 2px solid ${props.theme.colors.selected};
  `}
`

const ActionIcon = styled(FontAwesomeIcon)`
  color: ${(props) => props.theme.colors.neutral};
  height: 20px;
  width: 20px;
`

const CropperTouchDisabler = styled.div`
  height: 100%;
  position: absolute;
  width: 100%;
  z-index: 9;
`

const ImgBoxLength = "348px"

const CropperPanel = styled(Box).attrs({
  ml: { _: 0, tablet: 3 },
  overflow: "hidden",
})``

const InternalImageCropper = (props) => {
  const {
    pref,
    crop,
    cropperData,
    handwritingEngraving,
    isMobile,
    upload,
    uploadImage,
    onMobileCallback,
    onTipClick,
    selectedMetal,
    setCropperData,
    setChoice,
    thresholdValue,
  } = props
  if (!pref || !crop) return null

  var cacheKey = `image-${pref}-${crop.aspect_ratio_width}-${crop.aspect_ratio_height}`
  var cropperCacheKey = `cropper-${pref}-${crop.aspect_ratio_width}-${crop.aspect_ratio_height}`
  var image = props.image
  var cropperRef = useRef(null)

  const applyThresholdToImg = (thresholdValue) => {
    const cached = StorageHelper.exist(cacheKey)
      ? StorageHelper.get(cacheKey)
      : ""
    const target = uploadImage || cached
    if (target) {
      const img = new Image()
      img.src = target
      img.onload = () => {
        const pixels = getImgPixels(img)
        updateImgThreshold(pixels, thresholdValue)
        const thresholdImage = copyImg(pixels)
        setChoice(pref, {
          cropper_image: thresholdImage.toDataURL(),
          threshold_value: thresholdValue,
        })
      }
    }
  }

  if (StorageHelper.exist(cacheKey) && !props.image) {
    image = StorageHelper.get(cacheKey)
    // refresh the page, and the cached upload image (apply threshold)
    if (handwritingEngraving) {
      image = applyThresholdToImg(thresholdValue || thresholdSettings.MEDIUM)
    }
  }

  const cropper_options = {
    autoCropArea: 1,
    background: false,
    center: false,
    cropBoxMovable: false,
    cropBoxResizable: false,
    data: null,
    dragMode: "move",
    guides: false,
    highlight: false,
    modal: false,
    ready: (e) => {
      var cropCache = null
      // Mask image crop factor from product custom properties.
      // All crop params for masks in custom properties are adjusted to a 1024px image size
      const cropFactor = 1024 / e.target.cropper.getContainerData().width
      const cropBoxLeft = crop.crop_pos_x / cropFactor
      const cropBoxTop = crop.crop_pos_y / cropFactor
      const cropBoxWidth = crop.aspect_ratio_width / cropFactor
      const cropBoxHeight = crop.aspect_ratio_height / cropFactor

      if (StorageHelper.exist(cropperCacheKey)) {
        cropCache = StorageHelper.get(cropperCacheKey)
      }

      const {
        replaceImage,
        updateThreshold,
        data,
        canvasData,
        cropBoxData,
      } = e.target
      if (updateThreshold && data && canvasData && cropBoxData) {
        e.target.cropper.setData(data)
        e.target.cropper.setCanvasData(canvasData)
        e.target.cropper.setCropBoxData(cropBoxData)
      } else if (
        !replaceImage &&
        cropperData &&
        Object.keys(cropperData).length > 0
      ) {
        // temporary cropper data, which passed from parent component
        // it store the temporary cropper actions (e.g., zoom / rotate)
        e.target.cropper.setData(cropperData.data)
        e.target.cropper.setCanvasData(cropperData.canvasData)
        e.target.cropper.setCropBoxData(cropperData.cropBoxData)
      } else if (cropCache) {
        // read crop cache, which saved during upload api
        e.target.cropper.setData(cropCache.data)
        e.target.cropper.setCanvasData(cropCache.canvasData)
        e.target.cropper.setCropBoxData(cropCache.cropBoxData)
      } else {
        e.target.cropper.reset()
      }

      // adjust cropbox according to mask view crop params
      e.target.cropper.setCropBoxData({
        height: cropBoxHeight,
        left: cropBoxLeft,
        top: cropBoxTop,
        width: cropBoxWidth,
      })
      e.target.replaceImage = false
      e.target.updateThreshold = false
    },
    responsive: !Modernizr.touchevent,
    toggleDragModeOnDblclick: false,
    zoomOnTouch: false,
  }

  useEffect(() => {
    return () => {
      if (cropperRef.current?.cropper) {
        // we save cropper actions (e.g., zoom / rotate), for content index changed
        // such as read instructions, and then come back to cropper
        setCropperData({
          canvasData: cropperRef.current.cropper.getCanvasData(),
          cropBoxData: cropperRef.current.cropper.getCropBoxData(),
          data: cropperRef.current.cropper.getData(),
        })
      }
    }
  }, [])

  useEffect(() => {
    /**
     * Fix wrong `.cropper-crop-box` position when switched from desktop view to
     * mobile view by force resizing to ensure correct `.cropper-container` width and height
     */
    if (isMobile && cropperRef.current?.cropper?.ready) {
      cropperRef.current.cropper.resize()
    }
  }, [isMobile])

  const handleUpdateThreshold = (thresholdValue) => {
    if (!cropperRef.current) return

    cropperRef.current.updateThreshold = true
    cropperRef.current.canvasData = cropperRef.current.cropper.getCanvasData()
    cropperRef.current.cropBoxData = cropperRef.current.cropper.getCropBoxData()
    cropperRef.current.data = cropperRef.current.cropper.getData()
    applyThresholdToImg(thresholdValue)
  }

  const confirmButton = onMobileCallback ? (
    <SelectButton
      data-cy={`product-image-wizard-option-modal-confirm-btn-${pref}`}
      data-pref={pref}
      id={`${pref}_upload_button`}
      margin={"none;"}
      onClick={() => {
        upload(cropperRef.current?.cropper, handwritingEngraving)
      }}
      selected={true}
      type="button"
    >
      Confirm
    </SelectButton>
  ) : (
    <ConfirmButton
      data-pref={pref}
      id={`${pref}_upload_button`}
      onClick={() => upload(cropperRef.current?.cropper, handwritingEngraving)}
      type="button"
    >
      Confirm
    </ConfirmButton>
  )

  const thresholdOptions = [
    {
      content: "-3",
      id: `threshold-low-3-${pref}`,
      threshold: thresholdSettings.LOW_3,
    },
    {
      content: "-2",
      id: `threshold-low-2-${pref}`,
      threshold: thresholdSettings.LOW_2,
    },
    {
      content: "-1",
      id: `threshold-low-1-${pref}`,
      threshold: thresholdSettings.LOW_1,
    },
    {
      content: "0",
      id: `threshold-medium-${pref}`,
      threshold: thresholdSettings.MEDIUM,
    },
    {
      content: "1",
      id: `threshold-high-1-${pref}`,
      threshold: thresholdSettings.HIGH_1,
    },
    {
      content: "2",
      id: `threshold-high-2-${pref}`,
      threshold: thresholdSettings.HIGH_2,
    },
    {
      content: "3",
      id: `threshold-high-3-${pref}`,
      threshold: thresholdSettings.HIGH_3,
    },
  ]

  const thresholdButtons = (
    <Flex alignItems="center" gap="6px">
      <Img
        alt="Low Contrast"
        height="12"
        mr={2}
        src={require("images/product/icons/crescent-moon.svg")}
        width="12"
      />
      {thresholdOptions.map((t) => (
        <SquareActionButton
          data-cy={`threshold-btn-${t.id}`}
          id={t.id}
          key={t.id}
          onClick={() => {
            handleUpdateThreshold(t.threshold)
          }}
          selected={thresholdValue === t.threshold}
        >
          {t.content}
        </SquareActionButton>
      ))}
      <Img
        alt="High Contrast"
        height="16"
        ml={2}
        src={require("images/product/icons/sun-bright.svg")}
        width="16"
      />
    </Flex>
  )

  const originalImage = () => {
    const cached = StorageHelper.exist(cacheKey)
      ? StorageHelper.get(cacheKey)
      : ""
    const target = uploadImage || cached
    if (!target) return null

    return (
      <OriginalImageHidden>
        <img
          id={`${pref}-original-image`}
          src={target}
          style={{
            height: isMobile ? "100%" : ImgBoxLength,
            width: isMobile ? "100%" : ImgBoxLength,
          }}
        />
      </OriginalImageHidden>
    )
  }

  return (
    <Container>
      <ProductImageCropWrapper
        background={
          crop.use_imgsmbly && crop.transparent_img && crop.mask_bg
            ? `${crop.mask_bg}&sku=${selectedMetal}`
            : ""
        }
        id={`${pref}_crop_wrapper`}
      >
        <ProductImageCrop id={`${pref}_crop`}>
          {isMobile ? <CropperTouchDisabler /> : null}
          <CropOverlay>
            <img
              data-cy="product-photo-upload-modal-image-cropper"
              src={
                pref === "box"
                  ? require(`images/cart/boxes/${crop.mask}`)
                  : `${crop.mask}&sku=${selectedMetal}`
              }
              style={{
                height: isMobile ? "100%" : ImgBoxLength,
                width: "100%",
              }}
            />
          </CropOverlay>
          {handwritingEngraving && originalImage()}
          <Cropper
            ref={cropperRef}
            {...cropper_options}
            src={image}
            style={{
              height: isMobile ? "100%" : ImgBoxLength,
              width: isMobile ? "100%" : ImgBoxLength,
              zIndex: 3,
            }}
          />
          <img
            id={`${pref}_image`}
            src={require("images/icons/jewlr.gif")}
            style={{ display: "none", maxWidth: "100%" }}
          />

          {/* This is fixes an issue where if the same image is loaded into 2 different croppers, the second will have a 200px*100px crop area */}
          <img
            data-cy="product-photo-upload-modal-image-cropper"
            onLoad={() => cropperRef.current?.cropper.replace(image)}
            src={image}
            style={{ display: "none" }}
          />
        </ProductImageCrop>
        <CropperInstruction id="cropper-instruction">
          <p>
            Position your photo in the frame by using the controls below or
            pinch to zoom and swipe to move the photo left and right
          </p>
        </CropperInstruction>
      </ProductImageCropWrapper>
      <CropperPanel>
        <CropperEditHeading>
          <CropperActionTitle>
            Use the buttons to position your{" "}
            {handwritingEngraving ? "image" : "photo"} in the frame.
          </CropperActionTitle>
          {!onMobileCallback && (
            // Hidden on mobile half modal, because power ranger
            <CropTips data-pref={pref}>
              <span>
                <FontAwesomeIcon icon={faInfoCircle} />
                Tips
              </span>
            </CropTips>
          )}
        </CropperEditHeading>

        <CropperMobileActions>
          <Box className="fs-ignore-rage-clicks" mx="48px">
            <Flex gap={2} justifyContent="center" mb={2}>
              <ActionButton
                id={`move-left-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(-2.5, 0)}
              >
                <ActionIcon icon={faChevronLeft} />
              </ActionButton>
              <ActionButton
                id={`move-right-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(2.5, 0)}
              >
                <ActionIcon icon={faChevronRight} />
              </ActionButton>

              <ActionButton
                id={`move-up-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(0, -2.5)}
              >
                <ActionIcon icon={faChevronUp} />
              </ActionButton>
              <ActionButton
                id={`move-down-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(0, 2.5)}
              >
                <ActionIcon icon={faChevronDown} />
              </ActionButton>
            </Flex>
            <Flex gap={2} justifyContent="center">
              <ActionButton
                id={`rotate-left-${pref}`}
                onClick={() => cropperRef.current?.cropper.rotate(-2.5)}
              >
                <ActionIcon icon={faUndo} />
              </ActionButton>
              <ActionButton
                id={`rotate-right-${pref}`}
                onClick={() => cropperRef.current?.cropper.rotate(2.5)}
              >
                <ActionIcon icon={faRedo} />
              </ActionButton>

              <ActionButton
                id={`zoom-in-${pref}`}
                onClick={() => cropperRef.current?.cropper.zoom(0.05)}
              >
                <ActionIcon icon={faPlus} />
              </ActionButton>
              <ActionButton
                id={`zoom-out-${pref}`}
                onClick={() => cropperRef.current?.cropper.zoom(-0.05)}
              >
                <ActionIcon icon={faMinus} />
              </ActionButton>
            </Flex>
          </Box>

          <Typography
            color="black"
            fontWeight="300"
            letterSpacing="-0.02em"
            lineHeight="22px"
            my={2}
          >
            Use the buttons to position your{" "}
            {handwritingEngraving ? "image" : "photo"} in the frame.{" "}
          </Typography>

          {/* handwriting threshold */}
          {handwritingEngraving && (
            <Flex alignItems="center" gap="6px" justifyContent="center" mb={2}>
              {thresholdButtons}
            </Flex>
          )}
        </CropperMobileActions>
        <CropperActions className="fs-ignore-rage-clicks" mb={3}>
          <Flex gap="12px">
            <CropperActionPair>
              <ActionButton
                id={`move-left-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(-2.5, 0)}
              >
                <ActionIcon icon={faChevronLeft} />
              </ActionButton>
              <ActionButton
                id={`move-right-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(2.5, 0)}
              >
                <ActionIcon icon={faChevronRight} />
              </ActionButton>
            </CropperActionPair>

            <CropperActionPair>
              <ActionButton
                id={`move-up-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(0, -2.5)}
              >
                <ActionIcon icon={faChevronUp} />
              </ActionButton>
              <ActionButton
                id={`move-down-${pref}`}
                onClick={() => cropperRef.current?.cropper.move(0, 2.5)}
              >
                <ActionIcon icon={faChevronDown} />
              </ActionButton>
            </CropperActionPair>
          </Flex>

          <Flex gap="12px">
            <CropperActionPair>
              <ActionButton
                id={`rotate-left-${pref}`}
                onClick={() => cropperRef.current?.cropper.rotate(-2.5)}
              >
                <ActionIcon icon={faUndo} />
              </ActionButton>
              <ActionButton
                id={`rotate-right-${pref}`}
                onClick={() => cropperRef.current?.cropper.rotate(2.5)}
              >
                <ActionIcon icon={faRedo} />
              </ActionButton>
            </CropperActionPair>

            <CropperActionPair>
              <ActionButton
                id={`zoom-in-${pref}`}
                onClick={() => cropperRef.current?.cropper.zoom(0.05)}
              >
                <ActionIcon icon={faPlus} />
              </ActionButton>
              <ActionButton
                id={`zoom-out-${pref}`}
                onClick={() => cropperRef.current?.cropper.zoom(-0.05)}
              >
                <ActionIcon icon={faMinus} />
              </ActionButton>
            </CropperActionPair>
          </Flex>
        </CropperActions>

        {/* handwriting threshold */}
        {handwritingEngraving && (
          <>
            <Divider color="grey.10" mb={3} />
            <CropperActionTitle>
              Use controls below to adjust contrast of image.
            </CropperActionTitle>
            <CropperActionSubtitle mb={2}>
              Default starts at 0.
            </CropperActionSubtitle>
            <CropperActions gap="6px" mb={4}>
              {thresholdButtons}
            </CropperActions>
          </>
        )}
        <Flex
          flexDirection={{ _: "column", tablet: "row" }}
          gap={{ tablet: 1 }}
        >
          <Box mb={{ _: "12px", tablet: 0 }} width="100%">
            <PhotoUpload
              isReupload={true}
              onUpload={() => {
                if (cropperRef.current) {
                  // avoid temporary saved cropper data apply to the new image
                  cropperRef.current.replaceImage = true
                  cropperRef.current.cropper.reset()
                  setCropperData({})
                }
                // setTimeout(() => {})
              }}
              pref={pref}
              source="PDP"
            />
          </Box>
          <Box width="100%">{confirmButton}</Box>
        </Flex>
        <Footnote>
          <FootnoteText>
            Photo or image uploads that include inappropriate, explicit, or
            illegal content will not be accepted.
          </FootnoteText>
        </Footnote>
        <Box pt="12px">
          <TipText onClick={onTipClick}>
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>Read our tips for best results</span>
          </TipText>
        </Box>
      </CropperPanel>
    </Container>
  )
}

const mapStateToProps = (state, ownProps) => {
  let selectedMetal = ""
  if (typeof state.choices.sku !== "undefined") {
    selectedMetal = state.choices.sku.value
  }
  let uploadImage =
    state && state.choices && state.choices[ownProps.pref]
      ? state.choices[ownProps.pref].upload_image
      : null
  let image =
    state && state.choices && state.choices[ownProps.pref]
      ? state.choices[ownProps.pref].cropper_image
      : null
  let crop =
    state &&
    state.wizard &&
    state.wizard.data &&
    state.wizard.data[ownProps.pref] &&
    state.wizard.data[ownProps.pref].cropper
      ? state.wizard.data[ownProps.pref].cropper
      : null
  let style_code =
    state &&
    state.choices &&
    state.choices.style_code &&
    state.choices.style_code.value
      ? state.choices.style_code.value
      : null
  const handwritingEngraving =
    ownProps.pref && state.wizard.data[ownProps.pref]?.handwriting_engraving
  let thresholdValue = thresholdSettings.MEDIUM
  if (
    state &&
    state.choices &&
    state.choices[ownProps.pref] &&
    state.choices[ownProps.pref].threshold_value
  ) {
    thresholdValue = state.choices[ownProps.pref].threshold_value
  }
  return {
    crop,
    handwritingEngraving,
    image,
    isMobile: state.browser.is.mobile,
    pref: ownProps.pref,
    selectedMetal,
    style_code,
    thresholdValue,
    uploadImage,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    setChoice: (pref, data) => {
      dispatch(updateChoice(pref, data))
    },
    upload: (cropper, handwritingEngraving) => {
      dispatch(
        cropUploadUserImage(ownProps.pref, cropper, {
          handwritingEngraving,
        })
      )
    },
  }
}

InternalImageCropper.propTypes = {
  crop: PropTypes.object,
  cropperData: PropTypes.object,
  handwritingEngraving: PropTypes.bool,
  image: PropTypes.string,
  isMobile: PropTypes.bool,
  onMobileCallback: PropTypes.func,
  onTipClick: PropTypes.func,
  pref: PropTypes.string,
  selectedMetal: PropTypes.string,
  setChoice: PropTypes.func,
  setCropperData: PropTypes.func,
  thresholdValue: PropTypes.number,
  upload: PropTypes.func,
  uploadImage: PropTypes.string,
}

const ImageCropper = connect(
  mapStateToProps,
  mapDispatchToProps
)(InternalImageCropper)

export default ImageCropper
